import { getHomeV2Daypart, type HomeV2Daypart } from '@/lib/home/homeV2Background'
import type { CSSProperties } from 'react'

/** V2 typography — four-tier hierarchy over warm glass. */
export const homeV2Text = {
  /** Tier 1 — major headlines (warm white) */
  headline: '#FFF9F3',
  primary: '#FFFFFF',
  /** Tier 2 — section eyebrows / headers (warm cream) */
  section: '#FFD8A8',
  /** Tier 3 — body (soft white) */
  body: 'rgba(255, 248, 240, 0.90)',
  secondary: 'rgba(255, 248, 240, 0.88)',
  label: 'rgba(255, 255, 255, 0.92)',
  /** Tier 4 — metadata (60–75% white) */
  metadata: 'rgba(255, 255, 255, 0.72)',
  muted: 'rgba(255, 255, 255, 0.62)',
  link: 'rgba(255, 255, 255, 0.96)',
  accent: '#F5D0FE',
  warm: '#FFD8A8',
} as const

export type HomeV2StatusLevel = 'low' | 'moderate' | 'high' | 'very_high' | 'extreme' | 'unknown' | 'good' | 'warm' | 'hot' | 'none'

export type HomeV2StatusToken = {
  bg: string
  text: string
  border: string
  icon: string
}

/** Semantic walk-condition system — icons and values carry meaning at a glance. */
export const homeV2Semantic = {
  heat: {
    iconBase: '#FB923C',
    low: { bg: 'rgba(74, 222, 128, 0.20)', text: '#BBF7D0', border: 'rgba(74, 222, 128, 0.38)', icon: '#86EFAC' },
    moderate: { bg: 'rgba(251, 191, 36, 0.22)', text: '#FDE68A', border: 'rgba(251, 191, 36, 0.40)', icon: '#FBBF24' },
    high: { bg: 'rgba(248, 113, 113, 0.24)', text: '#FECACA', border: 'rgba(248, 113, 113, 0.42)', icon: '#F87171' },
    very_high: { bg: 'rgba(251, 146, 60, 0.26)', text: '#FED7AA', border: 'rgba(251, 146, 60, 0.44)', icon: '#FB923C' },
    extreme: { bg: 'rgba(239, 68, 68, 0.28)', text: '#FCA5A5', border: 'rgba(239, 68, 68, 0.48)', icon: '#EF4444' },
    unknown: { bg: 'rgba(255, 255, 255, 0.10)', text: 'rgba(255, 255, 255, 0.76)', border: 'rgba(255, 255, 255, 0.18)', icon: '#FB923C' },
  },
  paw: {
    iconBase: '#FACC15',
    low: { bg: 'rgba(74, 222, 128, 0.18)', text: '#BBF7D0', border: 'rgba(74, 222, 128, 0.36)', icon: '#86EFAC' },
    moderate: { bg: 'rgba(251, 191, 36, 0.20)', text: '#FDE68A', border: 'rgba(251, 191, 36, 0.38)', icon: '#FACC15' },
    high: { bg: 'rgba(248, 113, 113, 0.22)', text: '#FECACA', border: 'rgba(248, 113, 113, 0.40)', icon: '#F87171' },
    extreme: { bg: 'rgba(239, 68, 68, 0.26)', text: '#FCA5A5', border: 'rgba(239, 68, 68, 0.44)', icon: '#EF4444' },
    unknown: { bg: 'rgba(255, 255, 255, 0.10)', text: 'rgba(255, 255, 255, 0.76)', border: 'rgba(255, 255, 255, 0.18)', icon: '#FACC15' },
  },
  surface: {
    iconBase: '#38BDF8',
    good: { bg: 'rgba(34, 211, 238, 0.16)', text: '#A5F3FC', border: 'rgba(34, 211, 238, 0.34)', icon: '#67E8F9' },
    warm: { bg: 'rgba(251, 191, 36, 0.18)', text: '#FDE68A', border: 'rgba(251, 191, 36, 0.36)', icon: '#FBBF24' },
    hot: { bg: 'rgba(248, 113, 113, 0.22)', text: '#FECACA', border: 'rgba(248, 113, 113, 0.40)', icon: '#FCA5A5' },
    unknown: { bg: 'rgba(255, 255, 255, 0.10)', text: 'rgba(255, 255, 255, 0.76)', border: 'rgba(255, 255, 255, 0.18)', icon: '#38BDF8' },
  },
  pollen: {
    iconBase: '#C084FC',
    none: { bg: 'rgba(192, 132, 252, 0.14)', text: 'rgba(255,255,255,0.82)', border: 'rgba(192, 132, 252, 0.30)', icon: '#E9D5FF' },
    low: { bg: 'rgba(167, 139, 250, 0.16)', text: '#DDD6FE', border: 'rgba(167, 139, 250, 0.34)', icon: '#C4B5FD' },
    moderate: { bg: 'rgba(168, 85, 247, 0.18)', text: '#E9D5FF', border: 'rgba(168, 85, 247, 0.36)', icon: '#A855F7' },
    high: { bg: 'rgba(126, 34, 206, 0.22)', text: '#F3E8FF', border: 'rgba(126, 34, 206, 0.40)', icon: '#9333EA' },
    unknown: { bg: 'rgba(255, 255, 255, 0.10)', text: 'rgba(255, 255, 255, 0.76)', border: 'rgba(255, 255, 255, 0.18)', icon: '#C084FC' },
  },
  community: {
    label: '#F5D0FE',
    icon: 'rgba(245, 208, 254, 0.95)',
  },
  upgrade: {
    label: '#FFE4B5',
    accent: '#FFD8A8',
  },
} as const

function normalizeLevel(level: string | null | undefined): string {
  return (level ?? 'unknown').toLowerCase().replace(/\s+/g, '_')
}

export function homeV2HeatStatus(level: string | null | undefined): HomeV2StatusToken {
  const key = normalizeLevel(level)
  const levels = homeV2Semantic.heat as Record<string, HomeV2StatusToken>
  return levels[key] ?? homeV2Semantic.heat.unknown
}

export function homeV2PawStatus(level: string | null | undefined): HomeV2StatusToken {
  const key = normalizeLevel(level)
  const levels = homeV2Semantic.paw as Record<string, HomeV2StatusToken>
  return levels[key] ?? homeV2Semantic.paw.unknown
}

export function homeV2SurfaceStatus(tempF: number | null | undefined): HomeV2StatusToken {
  if (tempF == null || !Number.isFinite(tempF)) return homeV2Semantic.surface.unknown
  if (tempF >= 100) return homeV2Semantic.surface.hot
  if (tempF >= 85) return homeV2Semantic.surface.warm
  return homeV2Semantic.surface.good
}

/** Human-readable surface condition for metric row (matches approved reference). */
export function homeV2SurfaceDisplayLabel(tempF: number | null | undefined): string {
  if (tempF == null || !Number.isFinite(tempF)) return '—'
  if (tempF >= 100) return 'Hot'
  if (tempF >= 85) return 'Warm'
  return 'Good'
}

export function homeV2FormatPollenDisplay(level: string | null | undefined): string {
  if (!level?.trim()) return 'None'
  const normalized = level.trim().toLowerCase()
  if (normalized === 'none' || normalized === 'unknown') return 'None'
  return normalized.charAt(0).toUpperCase() + normalized.slice(1)
}

export function homeV2PollenStatus(level: string | null | undefined): HomeV2StatusToken {
  const key = normalizeLevel(level)
  const levels = homeV2Semantic.pollen as Record<string, HomeV2StatusToken>
  return levels[key] ?? homeV2Semantic.pollen.unknown
}

export function homeV2StatusPillStyle(token: HomeV2StatusToken): {
  backgroundColor: string
  color: string
  border: string
} {
  return {
    backgroundColor: token.bg,
    color: token.text,
    border: `1px solid ${token.border}`,
  }
}

/** @deprecated use homeV2StatusPillStyle(homeV2HeatStatus(level)) */
export function homeV2HeatPillStyle(level: string | null | undefined) {
  return homeV2StatusPillStyle(homeV2HeatStatus(level))
}

/** @deprecated use homeV2StatusPillStyle(homeV2PawStatus(level)) */
export function homeV2PawPillStyle(level: string | null | undefined) {
  return homeV2StatusPillStyle(homeV2PawStatus(level))
}

export const homeV2TextShadow = '0 1px 4px rgba(0, 0, 0, 0.32)'
export const homeV2HeroShadow = '0 2px 8px rgba(0, 0, 0, 0.36)'

const glassEdgeHighlight = 'inset 0 1px 0 rgba(255, 248, 238, 0.46)'
const warmInnerGlow =
  'inset 0 1px 0 rgba(255, 228, 188, 0.30), inset 0 0 56px rgba(212, 175, 95, 0.07)'
const softOuterShadow = '0 12px 36px rgba(10, 8, 10, 0.26), 0 3px 10px rgba(0, 0, 0, 0.12)'
const heroOuterShadow = '0 16px 48px rgba(10, 8, 10, 0.30), 0 4px 14px rgba(0, 0, 0, 0.14)'

/** V2 glass — neutral-warm midnight frost with champagne edges and warm gold luminance. */
export const homeV2Glass = {
  blur: '22px',
  pill: {
    background:
      'linear-gradient(165deg, rgba(30, 26, 28, 0.36) 0%, rgba(40, 34, 36, 0.22) 55%, rgba(50, 44, 46, 0.14) 100%)',
    border: 'rgba(255, 236, 210, 0.28)',
    shadow: `${glassEdgeHighlight}, ${warmInnerGlow}, ${softOuterShadow}`,
    radius: 'rounded-full',
  },
  surface: {
    background:
      'linear-gradient(165deg, rgba(28, 24, 26, 0.40) 0%, rgba(38, 32, 34, 0.26) 52%, rgba(48, 42, 44, 0.16) 100%)',
    border: 'rgba(255, 236, 210, 0.30)',
    shadow: `${glassEdgeHighlight}, ${warmInnerGlow}, ${softOuterShadow}`,
    radius: 'rounded-[32px]',
  },
  hero: {
    background:
      'linear-gradient(165deg, rgba(28, 24, 26, 0.46) 0%, rgba(36, 30, 32, 0.30) 50%, rgba(44, 38, 40, 0.18) 100%)',
    border: 'rgba(255, 236, 210, 0.32)',
    shadow: `${glassEdgeHighlight}, ${warmInnerGlow}, ${heroOuterShadow}`,
    radius: 'rounded-[32px]',
    ambientBloom:
      'radial-gradient(ellipse 95% 65% at 50% 0%, rgba(251, 191, 96, 0.14) 0%, transparent 70%)',
  },
  nested: {
    background:
      'linear-gradient(165deg, rgba(32, 28, 30, 0.32) 0%, rgba(42, 36, 38, 0.20) 100%)',
    border: 'rgba(255, 232, 198, 0.26)',
    shadow: `${glassEdgeHighlight}, ${warmInnerGlow}`,
    radius: 'rounded-[24px]',
  },
  whisper: {
    background:
      'linear-gradient(165deg, rgba(34, 30, 32, 0.28) 0%, rgba(44, 38, 40, 0.16) 100%)',
    border: 'rgba(255, 228, 188, 0.22)',
    shadow: `${glassEdgeHighlight}, ${warmInnerGlow}, 0 8px 22px rgba(10, 8, 10, 0.10)`,
    radius: 'rounded-[24px]',
  },
} as const

export type HomeV2GlassVariant = 'pill' | 'surface' | 'hero' | 'nested' | 'whisper'

export const homeV2Gradients = {
  warm: 'linear-gradient(135deg, #E86C8D 0%, #C45CFF 45%, #FF8C42 100%)',
  /** Sunrise — Pink → Orange → Gold (nav active, primary CTAs) */
  sunrise: 'linear-gradient(135deg, #F472B6 0%, #FB923C 52%, #FBBF24 100%)',
  twilight: 'linear-gradient(135deg, #9333EA 0%, #EC4899 52%, #6366F1 100%)',
  night: 'linear-gradient(135deg, #312E81 0%, #6B21A8 48%, #9333EA 100%)',
  play: 'linear-gradient(135deg, #A855F7 0%, #EC4899 100%)',
  parks: 'linear-gradient(135deg, #34D399 0%, #059669 100%)',
  picks: 'linear-gradient(135deg, #F472B6 0%, #FB7185 100%)',
  community: 'linear-gradient(135deg, #C084FC 0%, #A855F7 100%)',
  /** Outing decision intelligence — amber / gold / champagne (distinct from Picks pink). */
  decision:
    'linear-gradient(145deg, #FDE68A 0%, #FBBF24 42%, #D97706 100%)',
} as const

export function getHomeV2AccentGradient(daypart: HomeV2Daypart): string {
  switch (daypart) {
    case 'sunrise':
    case 'late_morning':
    case 'morning':
      return homeV2Gradients.sunrise
    case 'sunset':
    case 'evening':
      return homeV2Gradients.twilight
    case 'night':
      return homeV2Gradients.night
    default:
      return homeV2Gradients.warm
  }
}

export function getHomeV2AccentGradientForHour(hour: number): string {
  return getHomeV2AccentGradient(getHomeV2Daypart(hour))
}

export type HomeV2TimeGreeting = 'Morning' | 'Afternoon' | 'Evening'

export function getHomeV2TimeGreeting(hour: number): HomeV2TimeGreeting {
  if (hour >= 5 && hour < 12) return 'Morning'
  if (hour >= 12 && hour < 17) return 'Afternoon'
  return 'Evening'
}

export function buildHomeV2GreetingLine(hour: number, firstName?: string | null): string {
  const timeGreeting = getHomeV2TimeGreeting(hour)
  if (firstName?.trim()) {
    return `Good ${timeGreeting}, ${firstName.trim().split(/\s+/)[0]}`
  }
  return `Good ${timeGreeting}`
}

export function buildHomeV2HeroSubtitle(options: {
  dogName?: string | null
  canPersonalize: boolean
  hasDog: boolean
}): string {
  const dogName = options.dogName?.trim()
  if (options.hasDog && dogName) {
    return `${dogName} is ready for more adventures.`
  }
  if (options.canPersonalize) {
    return 'Add your dog to unlock personalized guidance for right now.'
  }
  return 'Discover what to do with your dog right now.'
}

/** Four-layer illuminated nav — radial glow, gradient pill, inner highlight, ambient bleed. */
export const homeV2Nav = {
  dockBackground: 'rgba(18, 14, 16, 0.78)',
  dockBorder: 'rgba(255, 232, 198, 0.22)',
  dockBlur: '26px',
  dockShadow:
    '0 20px 64px rgba(0, 0, 0, 0.52), 0 6px 18px rgba(0, 0, 0, 0.28), inset 0 1px 0 rgba(255, 210, 150, 0.12)',
  ambientGlow: 'radial-gradient(ellipse 90% 70% at 50% 100%, rgba(251, 146, 60, 0.28) 0%, transparent 72%)',
  activeGradient: homeV2Gradients.sunrise,
  radialGlow:
    'radial-gradient(circle at 50% 50%, rgba(251, 146, 60, 0.82) 0%, rgba(244, 114, 182, 0.48) 44%, transparent 76%)',
  innerHighlight: 'inset 0 1px 0 rgba(255, 255, 255, 0.48)',
  ambientBleed:
    '0 0 22px rgba(251, 146, 60, 0.55), 0 0 36px rgba(244, 114, 182, 0.22), 0 4px 16px rgba(255, 140, 66, 0.28)',
  spillGlow:
    'radial-gradient(ellipse 110% 90% at 50% 100%, rgba(251, 146, 60, 0.52) 0%, rgba(244, 114, 182, 0.24) 46%, transparent 76%)',
  spillGlowOuter:
    'radial-gradient(ellipse 120% 95% at 50% 100%, rgba(251, 191, 36, 0.24) 0%, rgba(244, 114, 182, 0.1) 52%, transparent 80%)',
  inactive: 'rgba(255, 255, 255, 0.50)',
  active: '#FFF9F3',
} as const

export const homeV2PhotoOverlay =
  'linear-gradient(to bottom, rgba(16, 12, 14, 0.52) 0%, rgba(22, 18, 20, 0.14) 34%, rgba(20, 16, 18, 0.18) 66%, rgba(12, 10, 12, 0.56) 100%)'

export const homeV2PhotoWarmTint =
  'radial-gradient(ellipse 120% 80% at 50% 20%, rgba(251, 191, 96, 0.11) 0%, transparent 55%), linear-gradient(to bottom, rgba(48, 40, 38, 0.10) 0%, transparent 45%, rgba(36, 30, 28, 0.12) 100%)'

export const homeV2Radius = {
  hero: 'rounded-[32px]',
  card: 'rounded-[32px]',
  nested: 'rounded-[24px]',
  well: 'rounded-[20px]',
  pill: 'rounded-full',
} as const

/** Single scroll-layer clearance above floating nav (document scroll content pad). */
export const homeV2ScrollBottomClearance = '6.25rem' as const

/** Shared horizontal inset for V2 photo tab page content (matches Home). */
export const homeV2PageInsetXClass = 'px-5' as const

/** Shared V2 section shell padding over photo backgrounds. */
export const homeV2SectionShellPaddingClass = 'p-5 sm:px-6' as const

/** Typography helper for Home V2 surfaces. */
export function homeV2Type(color: string) {
  return { color, textShadow: homeV2TextShadow }
}

/** Tier 2 section eyebrow styling. */
export function homeV2Eyebrow() {
  return {
    color: homeV2Text.section,
    textShadow: homeV2TextShadow,
    letterSpacing: '0.14em',
  } as const
}

/** Standard V2 eyebrow — 11px uppercase, 0.14em tracking (via homeV2Eyebrow). */
export const homeV2EyebrowClass = 'text-[11px] font-semibold uppercase' as const

export function homeV2EyebrowStyle(): CSSProperties {
  return { ...homeV2Type(homeV2Text.section), ...homeV2Eyebrow() }
}

/** Page/section titles (Profile tabs, major sections). */
export const homeV2SectionTitleClass = 'text-lg font-bold leading-snug sm:text-xl' as const

/** Card headlines (Pack Leader promo, preview cards). */
export const homeV2CardTitleClass = 'text-base font-bold leading-snug sm:text-lg' as const

/** Nested subsection titles inside a section card. */
export const homeV2SubsectionTitleClass = 'text-sm font-semibold leading-snug' as const

export const homeV2BodyClass = 'text-sm leading-relaxed' as const

export const homeV2MetadataClass = 'text-xs leading-relaxed' as const

export const homeV2ChipClass = 'rounded-full px-2.5 py-0.5 text-[11px] font-semibold' as const

/** Small status / “Coming Soon” chips on photo backgrounds. */
export function homeV2ComingSoonChipStyle(): CSSProperties {
  return homeV2StatusPillStyle(homeV2Semantic.heat.moderate)
}

/** Inline warning callouts (missing profile fields, etc.). */
export function homeV2WarningCalloutStyle(): CSSProperties {
  const t = homeV2Semantic.heat.moderate
  return {
    ...homeV2StatusPillStyle(t),
    color: homeV2Text.body,
  }
}

/** Transparent glass shell for Play / Parks / Picks / Profile over section photography. */
export function homeV2SectionShellStyle(): CSSProperties {
  const t = homeV2Glass.surface
  return {
    background: t.background,
    border: `1px solid ${t.border}`,
    boxShadow: t.shadow,
  }
}
